import React, { useEffect, useRef, useState } from "react";
import "./ITixpos.css";

/* ============================================================
   IMAGE – apni image public/iTiXBix_img/ me rakho aur naam yahin badlo
   ============================================================ */
const IMAGE = "/iTiXBix_img/Lightning-Fast.png";

/* ============================================================
   CONTENT  (yahin se edit karo)
   ============================================================ */
const HEAD = {
  id: "pos",
  pill: "QUICK SALES WORKSPACE (POS)",
  title: "Lightning-Fast Billing & Easy Product Identification",
  subtitle:
    "Speed up checkout queues with an intuitive workspace and instant camera-based product syncing.",
  tag: "QUICK POS",
};

const FEATURES = [
  {
    icon: "sync",
    title: "Product Image Sync",
    text: "Easy and fast product identification by syncing directly with product images.",
  },
  {
    icon: "camera",
    title: "Camera Integration",
    text: "Built-in mobile/device camera integration to capture and sync product images on the fly.",
  },
  {
    icon: "grid",
    title: "Product Cards & Category Filters",
    text: "Visual grid layout for fast item selection.",
  },
  {
    icon: "star",
    title: "Favorites & SKU Search",
    text: "Instant lookup for top-selling items.",
  },
  {
    icon: "barcode",
    title: "Barcode Scanner Support",
    text: "Seamless integration with USB and Bluetooth barcode scanners.",
  },
  {
    icon: "receipt",
    title: "Multi-Item Invoices",
    text: "Add multiple items with customer details and automatic GST calculations instantly.",
  },
];

/* ============================================================
   ICONS
   ============================================================ */
const ICONS = {
  sync: (
    <>
      <rect x="3" y="3" width="8" height="8" rx="1.5" />
      <path d="M15 5h6M15 9h4" />
      <path d="M3 15a6 6 0 0 0 10.5 3.5M21 19a6 6 0 0 0-10.5-3.5" />
      <path d="M13.5 21v-2.5H16M10.5 13v2.5H8" />
    </>
  ),
  camera: (
    <>
      <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z" />
      <circle cx="12" cy="13" r="3" />
    </>
  ),
  grid: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  star: (
    <path d="M12 2.5l2.9 6 6.6.9-4.8 4.6 1.2 6.5L12 17.3l-5.9 3.2 1.2-6.5L2.5 9.4l6.6-.9z" />
  ),
  barcode: (
    <>
      <path d="M3 5v14M7 5v14M11 5v14M15 5v14M18 5v14M21 5v14" />
    </>
  ),
  receipt: (
    <>
      <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1Z" />
      <path d="M15 8.5h-4.5a2 2 0 0 0 0 4h3a2 2 0 0 1 0 4H9M12 6.5v2M12 16.5v2" />
    </>
  ),
};

const Icon = ({ name, size = 28 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {ICONS[name]}
  </svg>
);

/* scroll-in hook */
const useInView = (threshold = 0.18) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setSeen(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setSeen(true);
          io.disconnect();
        }
      },
      { threshold }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [threshold]);
  return [ref, seen];
};

/* ============================================================
   SECTION
   ============================================================ */
const ITixPOS = () => {
  const [ref, seen] = useInView();

  return (
    <section ref={ref} id={HEAD.id} className={`pq ${seen ? "pq--in" : ""}`}>
      <div className="pq__container">
        {/* ---------- heading ---------- */}
        <header className="pq__head">
          <span className="pq__pill pq__r">{HEAD.pill}</span>
          <h2 className="pq__title pq__r" style={{ "--i": 1 }}>
            {HEAD.title}
          </h2>
          <span className="pq__underline" />
          <p className="pq__subtitle pq__r" style={{ "--i": 2 }}>
            {HEAD.subtitle}
          </p>
        </header>

        <div className="pq__layout">
          {/* ---------- feature tiles (left) ---------- */}
          <ul className="pq__tiles">
            {FEATURES.map((f, i) => (
              <li key={f.title} className="pq__tile pq__r pq__r--tile" style={{ "--i": i + 3 }}>
                <span className="pq__num" aria-hidden="true">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="pq__icon">
                  <Icon name={f.icon} />
                </span>
                <h3>{f.title}</h3>
                <p>{f.text}</p>
                <span className="pq__bar" aria-hidden="true" />
              </li>
            ))}
          </ul>

          {/* ---------- image (right) ---------- */}
          <div className="pq__media pq__r pq__r--media" style={{ "--i": 4 }}>
            <div className="pq__frame">
              <div className="pq__frame-inner">
                <span className="pq__tag">{HEAD.tag}</span>
                <div className="pq__shot">
                  <img src={IMAGE} alt={HEAD.title} loading="lazy" className="pq__img" />
                  <span className="pq__scan" aria-hidden="true" />
                  <i className="pq__corner pq__corner--tl" aria-hidden="true" />
                  <i className="pq__corner pq__corner--tr" aria-hidden="true" />
                  <i className="pq__corner pq__corner--bl" aria-hidden="true" />
                  <i className="pq__corner pq__corner--br" aria-hidden="true" />
                </div>
              </div>
            </div>
            <span className="pq__shadow" aria-hidden="true" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ITixPOS;